import { Event } from '../types/Event';
import { Timestamp, GeoPoint } from 'firebase/firestore';
import { imageService } from './imageService.ts';
import { getDoc, doc, collection, query, where, getDocs, deleteDoc } from 'firebase/firestore';
import { db } from '../firebase/config.ts';
import { classifyEventType, classifyEventTypeDetailed } from '../services/aiService.ts';
import { eventService } from './eventService.ts';
import { EventCategory } from '../constants/eventTypes.ts';

export interface FacebookJsonEvent {
  name: string;
  description: string;
  location?: {
    name: string;
    url?: string | null;
    address?: string;
    coordinates?: {
      latitude: number;
      longitude: number;
    };
  };
  date: string;
  url: string;
  imageUrl?: string;
  Image_gallery_url?: string[];
  event_type?: string;
  hostID?: string;
}

export interface TelAvivJsonEvent {
  place: string;
  name: string;
  description: string;
  event_url?: string;
  url?: string;
  imageUrl?: string;
  Image_gallery_url: string[];
  hostID?: string;
  location?: {
    name: string;
    url?: string | null;
    address?: string | null;
    coordinates?: {
      latitude: number | null;
      longitude: number | null;
    };
  };
  coordinates?: {
    latitude: number;
    longitude: number;
  };
  dates?: string[];
  is_recurring?: boolean;
  date_scraped?: string;
  event_type?: string;
  source?: string;
}

export interface CinemaJsonEvent {
  place: string;
  hostID: string;
  name: string;
  english_name: string;
  description: string;
  event_url: string;
  genre: string;
  poster_url: string;
  Image_gallery_url: string[];
  trailer_url?: string;
  duration: number;
  dates: {
    time: string;
    date: string;
    hall: string;
    is_premiere: boolean;
  }[];
  date_scraped: string;
  special_event: "movie";
  is_recurring: boolean;
}

export interface MevalimJsonEvent {
  name: string;
  description: string;
  date: string[];
  url?: string;
  imageUrl?: string;
  location?: {
    name: string;
    address?: string;
    url?: string;
    coordinates?: {
      latitude: number;
      longitude: number;
    };
  };
  source: string;
  hostID?: string;
  _localImageFiles?: File[];
}

export interface FunzingJsonEvent {
  url: string;
  name: string;
  description: string;
  hostID?: string;
  location: {
    name: string;
    address: string;
    url?: string | null;
    coordinates?: {
      latitude: number | null;
      longitude: number | null;
    };
  };
  date: string[];
  imageUrls: string[];
  source: 'funzing';
}

interface EventForDuplicateCheck {
  name: string;
  place?: string;
  date?: string[];
  location?: {
    name: string;
  };
}

interface ClassificationResult {
  category: EventCategory;
  type: string;
}

interface DuplicateCheckResult {
  isDuplicate: boolean;
  duplicates: {
    id: string;
    title: string;
    place: string;
    date: Date;
    createdAt: Date;
  }[];
  reviewReason?: {
    missingFields: string[];
    hasConflict: boolean;
    conflictingEvents: {
      title: string;
      place: string;
      time: Timestamp;
    }[];
  };
}

export const eventParsers = {
  /**
   * Parse Facebook event format
   */
  async parseFacebookEvent(jsonEvent: FacebookJsonEvent): Promise<Event> {
    try {
      console.log('Parsing Facebook event:', {
        name: jsonEvent.name,
        imageUrl: jsonEvent.imageUrl,
        location: jsonEvent.location,
        date: jsonEvent.date
      });

      // Get AI classification for event type
      console.log('Getting AI classification for event:', jsonEvent.name);
      const response = await fetch('https://us-central1-spontane-gog47y.cloudfunctions.net/classifyEventTypeDetailed', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Accept': 'application/json'
        },
        body: JSON.stringify({
          title: jsonEvent.name,
          description: jsonEvent.description || ''
        })
      });

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      const classification = await response.json();
      console.log('AI classification result:', classification);

      // Get coordinates
      const lat_long = new GeoPoint(
        jsonEvent.location?.coordinates?.latitude || 32.04081667021916,
        jsonEvent.location?.coordinates?.longitude || 34.76545583456755
      );

      // Parse the main date string with validation
      const mainDateTime = parseEventDateTime(jsonEvent.date);
      let startTimes: Timestamp[] = [];
      let endTimes: Timestamp[] | null = null;

      // Validate the parsed dates before using them
      if (mainDateTime.startTime && mainDateTime.startTime.toDate().getTime() > 0) {
        startTimes = mainDateTime.additionalStartTimes || [mainDateTime.startTime];
        endTimes = mainDateTime.additionalEndTimes || (mainDateTime.endTime ? [mainDateTime.endTime] : null);
      } else {
        // If invalid date, use current time
        const now = new Date();
        startTimes = [Timestamp.fromDate(now)];
        endTimes = [Timestamp.fromDate(new Date(now.getTime() + 2 * 60 * 60 * 1000))];
      }

      // Process images if they exist
      const processedImages = await imageService.fetchAndProcessImages([
        ...(jsonEvent.imageUrl ? [jsonEvent.imageUrl] : []),
        ...(jsonEvent.Image_gallery_url || [])
      ]);

      console.log('Processed images:', processedImages);

      const event = {
        title: jsonEvent.name,
        description: jsonEvent.description,
        place: jsonEvent.location?.name || 'Tel Aviv',
        category: classification.category as EventCategory,
        type: classification.type,
        event_type: classification.category as EventCategory,
        isPublic: true,
        lat_long,
        date: startTimes[0].toDate(),
        startDateTime: startTimes,
        endDateTime: endTimes,
        userId: 'K09eqzpzpOYAzcdl8k2vtujaJLO2',
        user_name: 'facebook_import',
        likes: 0,
        comments: 0,
        shares: 0,
        Image_gallery_url: processedImages.map(img => img.url),
        imageUrl: processedImages[0]?.url || '',
        poster_url: processedImages[0]?.url || '',
        event_url: jsonEvent.url || '',
        _localImageFiles: processedImages.map(img => img.file),
        is_recurring: startTimes.length > 1,
        source: 'facebook'
      };

      console.log('Created Facebook event object:', {
        title: event.title,
        date: event.date,
        startTimes: event.startDateTime.map(t => t.toDate()),
        endTimes: event.endDateTime?.map(t => t.toDate()),
        is_recurring: event.is_recurring,
        imageUrls: event.Image_gallery_url
      });

      return event as Event;
    } catch (error) {
      console.error('Error parsing Facebook event:', error);
      throw error;
    }
  },

  /**
   * Parse Tel Aviv event format
   */
  async parseTelAvivEvent(jsonEvent: TelAvivJsonEvent): Promise<Event | null> {
    try {
      // Skip events with empty location names
      if (!jsonEvent.location?.name || jsonEvent.location.name.trim() === '') {
        console.log('Skipping event due to empty location name:', jsonEvent.name);
        return null;
      }

      // Get AI classification for event type with retries
      console.log('Getting AI classification for event:', jsonEvent.name);
      let classification: { category: EventCategory; type: string } = {
        category: 'Social Meetups',
        type: 'Casual Hangouts'
      };
      let retryCount = 0;
      const MAX_RETRIES = 3;
      const BASE_DELAY = 2000; // 2 seconds base delay

      while (retryCount < MAX_RETRIES) {
        try {
          if (retryCount > 0) {
            const delay = BASE_DELAY * Math.pow(2, retryCount - 1);
            console.log(`Waiting ${delay}ms before retry ${retryCount + 1}`);
            await new Promise(resolve => setTimeout(resolve, delay));
          }

          const response = await fetch('https://us-central1-spontane-gog47y.cloudfunctions.net/classifyEventTypeDetailed', {
            method: 'POST',
            headers: {
              'Content-Type': 'application/json',
              'Accept': 'application/json'
            },
            body: JSON.stringify({
              title: jsonEvent.name,
              description: jsonEvent.description || ''
            })
          });

          if (!response.ok) {
            throw new Error(`HTTP error! status: ${response.status}`);
          }

          const result = await response.json();
          classification = {
            category: result.category as EventCategory,
            type: result.type
          };
          console.log('AI classification result:', classification);
          break;
        } catch (error) {
          console.error(`Error classifying event (Attempt ${retryCount + 1}):`, error);
          retryCount++;

          if (retryCount === MAX_RETRIES) {
            console.warn('Max retries reached, using fallback classification');
            classification = inferEventCategory(jsonEvent.name);
          }
        }
      }

      // Now we can safely use classification since it's guaranteed to be non-null
      const { category, type } = classification;

      // Process images first - filter for Tel Aviv URLs only
      const allImages = [
        jsonEvent.imageUrl,
        ...(jsonEvent.Image_gallery_url || [])
      ]
        .filter((url): url is string => Boolean(url && url.includes('tel-aviv.gov.il')))
        .map(url => {
          // Clean up URLs by removing any query parameters
          const cleanUrl = url.split('?')[0];
          console.log('Cleaned Tel Aviv image URL:', cleanUrl);
          return cleanUrl;
        });

      console.log('Tel Aviv images to process:', allImages);

      // Process images with error handling
      const imageUrls: string[] = [];
      const localFiles: File[] = [];

      for (const url of allImages) {
        try {
          console.log('Processing Tel Aviv image:', url);
          const processed = await imageService.fetchAndCompressImage(url, imageUrls.length);
          localFiles.push(processed.file);
          // Use the processed URL instead of the original URL
          imageUrls.push(url);  // Keep the original URL
          console.log('Successfully processed image:', {
            originalUrl: url,
            processedUrl: processed.url,
            size: processed.file.size,
            type: processed.file.type
          });
        } catch (error) {
          console.error('Error processing Tel Aviv image:', error);
          // Keep the original URL even if processing failed
          imageUrls.push(url);
        }
      }

      // Get coordinates
      let lat_long: GeoPoint;
      
      // If hostID is provided, use host coordinates
      if (jsonEvent.hostID) {
        lat_long = await getHostCoordinates(jsonEvent.hostID);
        console.log('Using host coordinates for hostID:', jsonEvent.hostID, lat_long);
      }
      // First try root-level coordinates if they are valid (not null)
      else if (jsonEvent.coordinates?.latitude && jsonEvent.coordinates?.longitude) {
        lat_long = new GeoPoint(
          jsonEvent.coordinates.latitude,
          jsonEvent.coordinates.longitude
        );
        console.log('Using root-level coordinates:', lat_long);
      } 
      // Then try location.coordinates if they are valid (not null)
      else if (jsonEvent.location?.coordinates?.latitude && jsonEvent.location?.coordinates?.longitude) {
        lat_long = new GeoPoint(
          jsonEvent.location.coordinates.latitude,
          jsonEvent.location.coordinates.longitude
        );
        console.log('Using location.coordinates:', lat_long);
      }
      // If no valid coordinates, try Google geocoding with any available location info
      else {
        const locationName = jsonEvent.location?.name || jsonEvent.place;
        if (!locationName) {
          throw new Error('No location information available to determine coordinates');
        }
        
        console.log('Searching coordinates for location:', locationName);
        const googleCoords = await getCoordinatesFromLocationName(locationName);
        
        if (!googleCoords) {
          throw new Error(`Could not determine coordinates for location: ${locationName}`);
        }
        
        lat_long = googleCoords;
        console.log('Using Google geocoded coordinates:', lat_long);
      }

      // Parse dates
      let startDateTime: Timestamp[];
      let endDateTime: Timestamp[];

      if (Array.isArray(jsonEvent.dates) && jsonEvent.dates.length > 0) {
        // Handle array of date strings
        const parsedDates = jsonEvent.dates.map(dateString => {
          try {
            return parseTelAvivDateTime(dateString);
          } catch (error) {
            console.error('Error parsing date:', error);
            const now = new Date();
            return {
              startTime: Timestamp.fromDate(now),
              endTime: Timestamp.fromDate(new Date(now.getTime() + 2 * 60 * 60 * 1000))
            };
          }
        });
        startDateTime = parsedDates.map(d => d.startTime);
        endDateTime = parsedDates.map(d => d.endTime);
      } else {
        // Default to current time if no dates provided
        const now = new Date();
        startDateTime = [Timestamp.fromDate(now)];
        endDateTime = [Timestamp.fromDate(new Date(now.getTime() + 2 * 60 * 60 * 1000))];
      }

      // Create and return the event object with AI-suggested category and type
      return {
        title: jsonEvent.name,
        description: jsonEvent.description,
        place: jsonEvent.location?.name || jsonEvent.place,
        category: category as EventCategory,
        type: type,
        event_type: category as EventCategory,
        isPublic: true,
        lat_long,
        date: startDateTime[0].toDate(),
        startDateTime,
        endDateTime,
        userId: 'K09eqzpzpOYAzcdl8k2vtujaJLO2',
        user_name: 'tel_aviv_import',
        likes: 0,
        comments: 0,
        shares: 0,
        Image_gallery_url: imageUrls,
        imageUrl: imageUrls[0] || '',
        poster_url: imageUrls[0] || '',
        event_url: jsonEvent.url || jsonEvent.event_url || '',
        _localImageFiles: localFiles,
        is_recurring: Boolean(jsonEvent.is_recurring),
        source: jsonEvent.source || ''
      };

    } catch (error) {
      console.error('Error parsing Tel Aviv event:', error);
      return null;
    }
  },

  /**
   * Parse Cinema event format (specialized for movies)
   */
  async parseCinemaEvent(jsonEvent: CinemaJsonEvent): Promise<Event> {
    try {
      // Process images first
      const allImages = [
        jsonEvent.poster_url,
        ...(jsonEvent.Image_gallery_url || [])
      ].filter(Boolean);

      // Use the imageService to fetch and compress images
      const processedImages = await imageService.fetchAndProcessImages(allImages);

      // Convert dates to Timestamps
      const startTimes: Timestamp[] = [];
      const endTimes: Timestamp[] = [];
      const halls: string[] = [];
      const is_premieres: boolean[] = [];

      // Process each screening
      jsonEvent.dates.forEach(screening => {
        const startTime = parseDateTime(screening.date, screening.time);
        const endTime = calculateEndTime(startTime, jsonEvent.duration);
        startTimes.push(startTime);
        endTimes.push(endTime);
        halls.push(screening.hall);
        is_premieres.push(screening.is_premiere);
      });

      // Create the event object
      const event: Event = {
        title: jsonEvent.name,
        description: jsonEvent.description,
        english_name: jsonEvent.english_name,
        genre: jsonEvent.genre,
        duration: jsonEvent.duration,
        place: jsonEvent.place,
        hostID: jsonEvent.hostID,
        event_url: jsonEvent.event_url,
        poster_url: jsonEvent.poster_url,
        Image_gallery_url: processedImages.map(img => img.url),
        _localImageFiles: processedImages.map(img => img.file),
        startDateTime: startTimes,
        endDateTime: endTimes,
        halls,
        is_premieres,
        isPublic: true,
        userId: 'K09eqzpzpOYAzcdl8k2vtujaJLO2',
        user_name: 'admin',
        likes: 0,
        comments: 0,
        shares: 0,
        lat_long: await getHostCoordinates(jsonEvent.hostID),
        // Set fixed category and type for cinema events
        category: 'Movie & Theatre',
        type: 'Movie Screenings',
        event_type: 'Movie & Theatre', // Add required event_type field
        is_recurring: jsonEvent.is_recurring
      };

      return event;
    } catch (error) {
      console.error('Error parsing cinema event:', error);
      throw error;
    }
  },

  /**
   * Parse Mevalim event format
   */
  async parseMevalimEvent(jsonEvent: MevalimJsonEvent): Promise<Event | null> {
    try {
      console.log('🎭 Parsing Mevalim event:', {
        name: jsonEvent.name,
        location: jsonEvent.location,
        imageUrl: jsonEvent.imageUrl
      });

      // Skip events with empty location names
      if (!jsonEvent.location?.name || jsonEvent.location.name.trim() === '') {
        console.log('Skipping event due to empty location name:', jsonEvent.name);
        return null;
      }

      // Check for duplicates before processing
      const duplicateCheck = await checkForDuplicate(jsonEvent);
      
      // Log duplicate information
      if (duplicateCheck.duplicates.length > 0) {
        console.log('Found duplicates for event:', jsonEvent.name, {
          duplicates: duplicateCheck.duplicates.map(d => ({
            id: d.id,
            title: d.title,
            place: d.place,
            date: d.date.toISOString(),
            createdAt: d.createdAt.toISOString()
          }))
        });
      }

      // If there are recent duplicates, skip the event
      if (duplicateCheck.isDuplicate) {
        console.log('Recent duplicate found, skipping:', jsonEvent.name);
        return null;
      }

      // Get AI classification for event type
      console.log('Getting AI classification for event:', jsonEvent.name);
      const response = await classifyEventType({
        title: jsonEvent.name,
        description: jsonEvent.description
      });
      
      const suggestedType: ClassificationResult = {
        category: response as EventCategory,
        type: response
      };
      console.log('AI suggested event type:', suggestedType);

      // Get coordinates using location name
      let lat_long: GeoPoint;
      
      // If hostID is provided, use host coordinates
      if (jsonEvent.hostID) {
        lat_long = await getHostCoordinates(jsonEvent.hostID);
        console.log('Using host coordinates for hostID:', jsonEvent.hostID, lat_long);
      }
      else if (jsonEvent.location) {
        console.log('📍 Location data:', JSON.stringify(jsonEvent.location, null, 2));
        
        // First check if we have coordinates in the location data
        if (jsonEvent.location.coordinates?.latitude && jsonEvent.location.coordinates?.longitude) {
          lat_long = new GeoPoint(
            jsonEvent.location.coordinates.latitude,
            jsonEvent.location.coordinates.longitude
          );
          console.log('📍 Using coordinates from location data:', lat_long);
        } else {
          // Extract location from Waze URL if available
          let wazeLocation: string | null = null;
          if (jsonEvent.location.url?.includes('waze.com')) {
            try {
              const wazeUrl = new URL(jsonEvent.location.url);
              const wazeQuery = wazeUrl.searchParams.get('q');
              if (wazeQuery) {
                wazeLocation = decodeURIComponent(wazeQuery).replace(/, ישראל$/, '');
                console.log('📍 Extracted Waze location:', wazeLocation);
              }
            } catch (error) {
              console.error('📍 Failed to parse Waze URL:', error);
            }
          }

          // Try location name first
          let coordinates: GeoPoint | null = null;
          if (jsonEvent.location.name) {
            coordinates = await getCoordinatesFromLocationName(jsonEvent.location.name);
            if (coordinates) {
              console.log('📍 Found coordinates using venue name:', {
                location: jsonEvent.location.name,
                coordinates
              });
            }
          }

          // If name lookup failed, try Waze location
          if (!coordinates && wazeLocation) {
            coordinates = await getCoordinatesFromLocationName(wazeLocation);
            if (coordinates) {
              console.log('📍 Found coordinates using Waze location:', {
                location: wazeLocation,
                coordinates
              });
            }
          }

          // If both failed, try address
          if (!coordinates && jsonEvent.location.address) {
            coordinates = await getCoordinatesFromLocationName(jsonEvent.location.address);
            if (coordinates) {
              console.log('📍 Found coordinates using address:', {
                address: jsonEvent.location.address,
                coordinates
              });
            }
          }

          if (coordinates) {
            lat_long = coordinates;
          } else {
            throw new Error('Could not determine coordinates for location');
          }
        }
      } else {
        throw new Error('No location information available');
      }

      // Process images
      const imageUrls: string[] = [];
      const localFiles: File[] = [];

      if (jsonEvent.imageUrl) {
        console.log('Found Mevalim image URL:', jsonEvent.imageUrl);
        try {
          const processed = await imageService.fetchAndCompressImage(jsonEvent.imageUrl, 0);
          localFiles.push(processed.file);
          imageUrls.push(jsonEvent.imageUrl);
          console.log('Successfully processed main image:', {
            size: processed.file.size,
            type: processed.file.type
          });
        } catch (error) {
          console.error('Error processing Mevalim image:', error);
          // Keep the original URL even if processing failed
          imageUrls.push(jsonEvent.imageUrl);
        }
      }

      // Parse the date string (format: "2025-01-26 20:30")
      const dateString = jsonEvent.date[0];
      const [datePart, timePart] = dateString.split(' ');
      const [year, month, day] = datePart.split('-').map(Number);
      const [hours, minutes] = timePart.split(':').map(Number);
      
      const startDateTime = new Date(year, month - 1, day, hours, minutes);
      const startTime = Timestamp.fromDate(startDateTime);

      // Create end time 2 hours after start time
      const endDateTime = new Date(startDateTime.getTime() + 2 * 60 * 60 * 1000);
      const endTime = Timestamp.fromDate(endDateTime);

      const event = {
        title: jsonEvent.name,
        description: jsonEvent.description,
        place: jsonEvent.location?.name || 'Tel Aviv',
        category: suggestedType.category as EventCategory,
        type: suggestedType.type,
        event_type: suggestedType.category as EventCategory,
        isPublic: true,
        lat_long,
        date: startDateTime,
        startDateTime: [startTime],
        endDateTime: [endTime],
        userId: 'K09eqzpzpOYAzcdl8k2vtujaJLO2',
        user_name: 'mevalim_import',
        likes: 0,
        comments: 0,
        shares: 0,
        Image_gallery_url: imageUrls,
        imageUrl: imageUrls[0] || '',
        poster_url: imageUrls[0] || '',
        event_url: jsonEvent.url || '',
        _localImageFiles: localFiles,
        source: jsonEvent.source || '',
        _reviewReason: duplicateCheck.reviewReason
      };

      console.log('Created Mevalim event object:', {
        title: event.title,
        imageUrl: event.imageUrl,
        Image_gallery_url: event.Image_gallery_url,
        _localImageFiles: event._localImageFiles?.length,
        coordinates: event.lat_long
      });

      // Auto-upload the event
      try {
        const { _localImageFiles, ...eventToCreate } = { 
          ...event,
          Image_gallery_url: [], // Initialize with empty array
          poster_url: '',       // Initialize with empty string
          imageUrl: ''         // Initialize with empty string
        };
        
        // First create the event document to get its ID
        const eventId = await eventService.createEvent(eventToCreate, []);
        console.log('Created event with ID:', eventId);

        // Upload and process images if we have any
        if (event._localImageFiles?.length) {
          try {
            const uploadedUrls = await imageService.processImages(event._localImageFiles, eventId);
            console.log('Successfully uploaded images:', uploadedUrls);

            // Update the event with the image URLs
            await eventService.updateEvent(eventId, {
              Image_gallery_url: uploadedUrls,
              poster_url: uploadedUrls[0] || '',
              imageUrl: uploadedUrls[0] || ''
            });
            console.log('Updated event with image URLs');
          } catch (error) {
            console.error('Error uploading images:', error);
          }
        }

        return null; // Return null since we've already uploaded it
      } catch (error) {
        console.error('Error auto-uploading event:', error);
        return event; // Return the event object only if auto-upload fails
      }

    } catch (error) {
      console.error('Error parsing Mevalim event:', error);
      return null;
    }
  },

  /**
   * Parse Funzing event format
   */
  async parseFunzingEvent(jsonEvent: FunzingJsonEvent): Promise<Event> {
    try {
      console.log('Parsing Funzing event:', {
        name: jsonEvent.name,
        imageUrls: jsonEvent.imageUrls
      });

      // Get coordinates using Google Places API
      let lat_long: GeoPoint;
      
      // If hostID is provided, use host coordinates
      if (jsonEvent.hostID) {
        lat_long = await getHostCoordinates(jsonEvent.hostID);
        console.log('Using host coordinates for hostID:', jsonEvent.hostID, lat_long);
      }
      else if (jsonEvent.location?.coordinates?.latitude && jsonEvent.location?.coordinates?.longitude) {
        lat_long = new GeoPoint(
          jsonEvent.location.coordinates.latitude,
          jsonEvent.location.coordinates.longitude
        );
        console.log('Using provided coordinates:', lat_long);
      } else if (jsonEvent.location?.name || jsonEvent.location?.address) {
        const locationQuery = `${jsonEvent.location.name}, ${jsonEvent.location.address}`;
        console.log('Searching coordinates for location:', locationQuery);
        const googleCoords = await getCoordinatesFromLocationName(locationQuery);
        if (googleCoords) {
          lat_long = googleCoords;
          console.log('Using Google geocoded coordinates:', lat_long);
        } else {
          throw new Error('Could not determine coordinates for location');
        }
      } else {
        throw new Error('No location information available');
      }

      // Process dates with improved Hebrew date parsing and next-day handling
      const parsedDates = jsonEvent.date.map(dateStr => {
        try {
          // Format: "יום חמישי, 30.01.2025, 17:15 - 18:00"
          const [, datePart, timePart] = dateStr.split(', ');
          if (!datePart || !timePart) {
            throw new Error(`Invalid date format: ${dateStr}`);
          }

          // Parse date components
          const [day, month, year] = datePart.split('.').map(Number);
          if (!day || !month || !year) {
            throw new Error(`Invalid date components: ${datePart}`);
          }

          // Parse time components
          const [startTime, endTime] = timePart.split(' - ');
          if (!startTime || !endTime) {
            throw new Error(`Invalid time format: ${timePart}`);
          }

          const [startHour, startMinute] = startTime.split(':').map(Number);
          const [endHour, endMinute] = endTime.split(':').map(Number);

          if (isNaN(startHour) || isNaN(startMinute) || isNaN(endHour) || isNaN(endMinute)) {
            throw new Error(`Invalid time components: ${timePart}`);
          }

          // Create start date
          const startDate = new Date(year, month - 1, day, startHour, startMinute);

          // Create end date, handling next-day events
          const endDate = new Date(year, month - 1, day, endHour, endMinute);
          if (endHour < startHour || (endHour === startHour && endMinute < startMinute)) {
            // If end time is earlier than start time, it means the event ends the next day
            endDate.setDate(endDate.getDate() + 1);
          }

          // Validate dates
          if (isNaN(startDate.getTime()) || isNaN(endDate.getTime())) {
            throw new Error(`Invalid date objects created from: ${dateStr}`);
          }

          return {
            startTime: Timestamp.fromDate(startDate),
            endTime: Timestamp.fromDate(endDate)
          };
        } catch (error) {
          console.error(`Error parsing date string: ${dateStr}`, error);
          // Return current date as fallback
          const now = new Date();
          return {
            startTime: Timestamp.fromDate(now),
            endTime: Timestamp.fromDate(new Date(now.getTime() + 60 * 60 * 1000)) // 1 hour later
          };
        }
      });

      // Process images
      const processedImages = await imageService.fetchAndProcessImages(jsonEvent.imageUrls);

      const event: Event = {
        title: jsonEvent.name,
        description: jsonEvent.description,
        place: jsonEvent.location.name,
        event_type: 'Other', // Will be classified by AI
        isPublic: true,
        lat_long,
        date: parsedDates[0].startTime.toDate(),
        startDateTime: parsedDates.map(d => d.startTime),
        endDateTime: parsedDates.map(d => d.endTime),
        userId: 'K09eqzpzpOYAzcdl8k2vtujaJLO2',
        user_name: 'funzing_import',
        likes: 0,
        comments: 0,
        shares: 0,
        Image_gallery_url: processedImages.map(img => img.url),
        imageUrl: processedImages[0]?.url || '',
        poster_url: processedImages[0]?.url || '',
        event_url: jsonEvent.url,
        _localImageFiles: processedImages.map(img => img.file)
      };

      console.log('Created Funzing event object:', {
        title: event.title,
        imageUrl: event.imageUrl,
        Image_gallery_url: event.Image_gallery_url,
        _localImageFiles: event._localImageFiles?.length,
        dates: {
          start: Array.isArray(event.startDateTime) ? event.startDateTime.map(d => d.toDate()) : [],
          end: Array.isArray(event.endDateTime) ? event.endDateTime.map(d => d.toDate()) : []
        }
      });

      return event;
    } catch (error) {
      console.error('Error parsing Funzing event:', error);
      throw error;
    }
  },
};

// Helper functions
const parseEventDateTime = (dateString: string): { 
  startTime: Timestamp; 
  endTime: Timestamp | null;
  additionalStartTimes?: Timestamp[];
  additionalEndTimes?: Timestamp[];
} => {
  try {
    // Handle case where no date string is provided or it's not a string
    if (!dateString || typeof dateString !== 'string') {
      console.warn('Invalid or missing date string:', dateString);
      return {
        startTime: Timestamp.now(),
        endTime: null
      };
    }

    // First check if this is a date with time (contains 'at')
    if (dateString.includes(' at ')) {
      // Split into date part and time part
      const [datePart, timePartWithIST] = dateString.split(' at ').map(s => s.trim());
      if (!timePartWithIST) {
        throw new Error('Invalid date format - empty time part');
      }

      // Remove IST from the end if it exists
      const timePart = timePartWithIST.replace(/\s*IST\s*$/, '').trim();

      // Check if this is a multi-day range
      if (datePart.includes('–')) {
        // Handle multi-day range format
        const [startDateStr, endDateStr] = datePart.split('–').map(s => s.trim());
        
        // Parse start date/time
        const startTimeMatch = timePart.match(/^(\d{1,2}:\d{2}\s*(?:AM|PM))/);
        if (!startTimeMatch) {
          throw new Error('Invalid start time format');
        }
        const startTimeStr = startTimeMatch[1];
        const startDateTime = new Date(`${startDateStr}, 2025 ${startTimeStr}`);
        
        // Convert to 24-hour format if needed
        let dailyStartHour = 0;
        let dailyStartMinute = 0;
        if (startTimeStr.includes('PM')) {
          const matches = startTimeStr.match(/(\d+):(\d+)/);
          if (matches) {
            const [, hour, minute] = matches;
            const hourNum = parseInt(hour);
            dailyStartHour = hourNum !== 12 ? hourNum + 12 : 12;
            dailyStartMinute = parseInt(minute);
            if (hourNum !== 12) {
              startDateTime.setHours(hourNum + 12, parseInt(minute));
            }
          }
        } else if (startTimeStr.includes('AM') && startTimeStr.match(/^12:/)) {
          startDateTime.setHours(0);
          dailyStartHour = 0;
        } else {
          const matches = startTimeStr.match(/(\d+):(\d+)/);
          if (matches) {
            const [, hour, minute] = matches;
            dailyStartHour = parseInt(hour);
            dailyStartMinute = parseInt(minute);
          }
        }

        // Parse end date/time
        const endTimeMatch = timePart.match(/–\s*(\d{1,2}:\d{2}\s*(?:AM|PM))/);
        if (!endTimeMatch) {
          throw new Error('Invalid end time format');
        }
        const endTimeStr = endTimeMatch[1];
        const endDateTime = new Date(`${endDateStr}, 2025 ${endTimeStr}`);
        
        // Convert to 24-hour format if needed
        let dailyEndHour = 0;
        let dailyEndMinute = 0;
        if (endTimeStr.includes('PM')) {
          const matches = endTimeStr.match(/(\d+):(\d+)/);
          if (matches) {
            const [, hour, minute] = matches;
            const hourNum = parseInt(hour);
            dailyEndHour = hourNum !== 12 ? hourNum + 12 : 12;
            dailyEndMinute = parseInt(minute);
            if (hourNum !== 12) {
              endDateTime.setHours(hourNum + 12, parseInt(minute));
            }
          }
        } else if (endTimeStr.includes('AM') && endTimeStr.match(/^12:/)) {
          endDateTime.setHours(0);
          dailyEndHour = 0;
        } else {
          const matches = endTimeStr.match(/(\d+):(\d+)/);
          if (matches) {
            const [, hour, minute] = matches;
            dailyEndHour = parseInt(hour);
            dailyEndMinute = parseInt(minute);
          }
        }

        // If end date is earlier than start date, it might be in the next year
        if (endDateTime < startDateTime) {
          endDateTime.setFullYear(endDateTime.getFullYear() + 1);
        }

        // Create time slots for each day
        const startTimes: Timestamp[] = [];
        const endTimes: Timestamp[] = [];
        
        let currentDate = new Date(startDateTime);
        while (currentDate <= endDateTime) {
          // Create new date objects for start and end times of this day
          const dayStart = new Date(currentDate);
          dayStart.setHours(dailyStartHour, dailyStartMinute, 0, 0);
          
          const dayEnd = new Date(currentDate);
          dayEnd.setHours(dailyEndHour, dailyEndMinute, 0, 0);
          
          // If end time is earlier than start time on the same day, it means it ends the next day
          if (dayEnd < dayStart) {
            dayEnd.setDate(dayEnd.getDate() + 1);
          }
          
          startTimes.push(Timestamp.fromDate(dayStart));
          endTimes.push(Timestamp.fromDate(dayEnd));
          
          // Move to next day
          currentDate.setDate(currentDate.getDate() + 1);
        }

        console.log('Created daily time slots:', {
          dateString,
          slots: startTimes.map((st, i) => ({
            start: st.toDate().toLocaleString(),
            end: endTimes[i].toDate().toLocaleString()
          }))
        });

        return {
          startTime: startTimes[0],
          endTime: endTimes[endTimes.length - 1],
          additionalStartTimes: startTimes,
          additionalEndTimes: endTimes
        };
      } else {
        // Single day format - check for time range
        const timeRangeParts = timePart.split('–').map(t => t.trim());
        
        if (timeRangeParts.length === 2) {
          const [startTimeStr, endTimeStr] = timeRangeParts;
          
          // Create start date with explicit year
          const startDateTime = new Date(`${datePart}, 2025 ${startTimeStr}`);
          
          // Handle PM for start time
          if (startTimeStr.includes('PM')) {
            const matches = startTimeStr.match(/(\d+):(\d+)/);
            if (matches) {
              const [, hour, minute] = matches;
              const hourNum = parseInt(hour);
              if (hourNum !== 12) {
                startDateTime.setHours(hourNum + 12, parseInt(minute));
              }
            }
          } else if (startTimeStr.includes('AM') && startTimeStr.match(/^12:/)) {
            startDateTime.setHours(0);
          }
          
          // Create end date (copy of start date)
          const endDateTime = new Date(startDateTime);
          
          // Handle PM for end time
          if (endTimeStr.includes('PM')) {
            const matches = endTimeStr.match(/(\d+):(\d+)/);
            if (matches) {
              const [, hour, minute] = matches;
              const hourNum = parseInt(hour);
              endDateTime.setHours(hourNum !== 12 ? hourNum + 12 : 12, parseInt(minute));
            }
          } else if (endTimeStr.includes('AM') && endTimeStr.match(/^12:/)) {
            endDateTime.setHours(0);
          } else {
            const matches = endTimeStr.match(/(\d+):(\d+)/);
            if (matches) {
              const [, hour, minute] = matches;
              endDateTime.setHours(parseInt(hour), parseInt(minute));
            }
          }

          console.log('Parsed single day with time range:', {
            original: dateString,
            start: startDateTime.toLocaleString(),
            end: endDateTime.toLocaleString()
          });

          return {
            startTime: Timestamp.fromDate(startDateTime),
            endTime: Timestamp.fromDate(endDateTime)
          };
        }

        // Single time format with explicit year
        const startDateTime = new Date(`${datePart}, 2025 ${timePart}`);
        
        // Handle PM for single time
        if (timePart.includes('PM')) {
          const matches = timePart.match(/(\d+):(\d+)/);
          if (matches) {
            const [, hour, minute] = matches;
            const hourNum = parseInt(hour);
            if (hourNum !== 12) {
              startDateTime.setHours(hourNum + 12, parseInt(minute));
            }
          }
        } else if (timePart.includes('AM') && timePart.match(/^12:/)) {
          startDateTime.setHours(0);
        }

        // Default 2 hour duration for single time
        const endDateTime = new Date(startDateTime.getTime() + 2 * 60 * 60 * 1000);

        console.log('Parsed single time:', {
          original: dateString,
          start: startDateTime.toLocaleString(),
          end: endDateTime.toLocaleString()
        });

        return {
          startTime: Timestamp.fromDate(startDateTime),
          endTime: Timestamp.fromDate(endDateTime)
        };
      }
    }

    console.warn('Unrecognized date format:', dateString);
    return {
      startTime: Timestamp.now(),
      endTime: null
    };
  } catch (error) {
    console.error('Error parsing event date time:', error, dateString);
    return {
      startTime: Timestamp.now(),
      endTime: null
    };
  }
};

const parseDateTime = (date: string, time: string): Timestamp => {
  try {
    if (!date || !time) {
      console.warn('Missing date or time:', { date, time });
      return Timestamp.fromDate(new Date());
    }
    
    // Handle Hebrew/English time formats
    const cleanTime = time.replace(/[^\d:]/g, '');
    const [hours, minutes] = cleanTime.split(':').map(Number);
    
    // Parse date (assuming format YYYY-MM-DD)
    const [year, month, day] = date.split('-').map(Number);
    
    // Validate date components
    if (!year || !month || !day || isNaN(hours) || isNaN(minutes)) {
      console.warn('Invalid date/time components:', { year, month, day, hours, minutes });
      return Timestamp.fromDate(new Date()); // Return current date as fallback
    }
    
    const dateObj = new Date(year, month - 1, day, hours, minutes);
    
    // Validate the created date
    if (isNaN(dateObj.getTime())) {
      console.warn('Invalid date created:', dateObj);
      return Timestamp.fromDate(new Date()); // Return current date as fallback
    }
    
    return Timestamp.fromDate(dateObj);
  } catch (error) {
    console.error('Error parsing date/time:', error);
    return Timestamp.fromDate(new Date()); // Return current date as fallback
  }
};

const getHostCoordinates = async (hostId: string): Promise<GeoPoint> => {
  try {
    const hostDoc = await getDoc(doc(db, 'Hosts', hostId));
    if (hostDoc.exists()) {
      const hostData = hostDoc.data();
      if (hostData.latLong instanceof GeoPoint) {
        return hostData.latLong;
      }
    }
    // If host not found or no coordinates, throw error
    throw new Error(`No valid coordinates found for host: ${hostId}`);
  } catch (error) {
    console.error('Error fetching host coordinates:', error);
    throw error;
  }
};

const parseTelAvivDateTime = (dateString: string): { startTime: Timestamp; endTime: Timestamp } => {
  try {
    // Format: "DD.M.YY HH:mm-HH:mm"
    const [datePart, timePart] = dateString.split(' ');
    const [startTime, endTime] = timePart.split('-');
    
    // Parse date components
    const [day, month, yearShort] = datePart.split('.').map(Number);
    const year = 2000 + yearShort; // Convert YY to YYYY
    
    // Parse time components
    const [startHour, startMinute] = startTime.split(':').map(Number);
    const [endHour, endMinute] = endTime.split(':').map(Number);
    
    // Create Date objects
    const startDate = new Date(year, month - 1, day, startHour, startMinute);
    let endDate = new Date(year, month - 1, day, endHour, endMinute);
    
    // If end time is earlier than start time, it means the event ends the next day
    if (endDate < startDate) {
      endDate = new Date(year, month - 1, day + 1, endHour, endMinute);
    }

    // Validate the dates
    if (isNaN(startDate.getTime()) || isNaN(endDate.getTime())) {
      throw new Error('Invalid date objects created');
    }
    
    return {
      startTime: Timestamp.fromDate(startDate),
      endTime: Timestamp.fromDate(endDate)
    };
  } catch (error) {
    console.error('Error parsing Tel Aviv date time:', error, dateString);
    const now = new Date();
    return {
      startTime: Timestamp.fromDate(now),
      endTime: Timestamp.fromDate(new Date(now.getTime() + 2 * 60 * 60 * 1000))
    };
  }
};

const getCoordinatesFromLocationName = async (locationName: string): Promise<GeoPoint | null> => {
  try {
    console.log('🔍 STARTING GOOGLE GEOCODING for:', locationName);
    
    // Add Tel Aviv to the search query for better results
    const searchQuery = `${locationName}, Tel Aviv, Israel`;
    console.log('🌍 Geocoding search query:', searchQuery);

    const url = `https://maps.googleapis.com/maps/api/geocode/json?address=${encodeURIComponent(searchQuery)}&key=AIzaSyCRa3fwxFQRctwLAh_784aAin9hE5SaIik`;
    console.log('🌍 Google Maps API request URL:', url);

    const response = await fetch(url);
    
    if (!response.ok) {
      console.error(`🚫 Google Geocoding API failed with status: ${response.status}`);
      throw new Error(`Geocoding failed: ${response.statusText}`);
    }

    const data = await response.json();
    console.log('🌍 Google Geocoding API response:', JSON.stringify(data, null, 2));

    if (data.status === 'OK' && data.results.length > 0) {
      const location = data.results[0].geometry.location;
      console.log('✅ Google Geocoding found coordinates:', location);
      return new GeoPoint(location.lat, location.lng);
    } else {
      console.error('❌ Google Geocoding found no results for:', locationName);
      console.error('❌ Google Geocoding API status:', data.status);
      return null;
    }
  } catch (error) {
    console.error('💥 Google Geocoding API error:', error);
    return null;
  }
};

const checkForDuplicate = async (event: EventForDuplicateCheck): Promise<DuplicateCheckResult> => {
  try {
    const eventName = event.name.toLowerCase();
    const eventPlace = (event.place || event.location?.name || '').toLowerCase();
    const eventDates = event.date?.map(d => new Date(d)) || [];

    // Initialize review reason
    const reviewReason: NonNullable<DuplicateCheckResult['reviewReason']> = {
      missingFields: [],
      hasConflict: false,
      conflictingEvents: []
    };

    // Check for missing required fields
    if (!eventPlace) reviewReason.missingFields.push('Location');
    if (eventDates.length === 0) reviewReason.missingFields.push('Date');
    if (!eventName) reviewReason.missingFields.push('Title');

    if (eventDates.length === 0) {
      console.log('No dates provided for duplicate check:', eventName);
      return { 
        isDuplicate: false, 
        duplicates: [],
        reviewReason
      };
    }

    // Query for events with the same name and place
    const eventsRef = collection(db, 'posts');
    const q = query(
      eventsRef,
      where('title', '==', eventName),
      where('place', '==', eventPlace)
    );
    
    const snapshot = await getDocs(q);
    
    // If no events found with same name and place, it's not a duplicate
    if (snapshot.empty) {
      return { 
        isDuplicate: false, 
        duplicates: [],
        reviewReason
      };
    }

    const duplicates: DuplicateCheckResult['duplicates'] = [];
    const oneDayAgo = new Date();
    oneDayAgo.setDate(oneDayAgo.getDate() - 1);

    // Check each potential duplicate
    for (const doc of snapshot.docs) {
      const existingEvent = doc.data();
      
      // Get all time slots from the existing event
      let existingTimeSlots: Date[] = [];
      if (Array.isArray(existingEvent.startDateTime)) {
        existingTimeSlots = existingEvent.startDateTime.map(timestamp => 
          timestamp instanceof Timestamp 
            ? new Date(timestamp.seconds * 1000)
            : new Date(timestamp)
        );
      } else if (existingEvent.startDateTime) {
        const timestamp = existingEvent.startDateTime;
        existingTimeSlots = [
          timestamp instanceof Timestamp 
            ? new Date(timestamp.seconds * 1000)
            : new Date(timestamp)
        ];
      }

      // Check if any of the time slots overlap
      const hasOverlappingSlot = eventDates.some(newDate => 
        existingTimeSlots.some(existingDate => {
          // Compare hours and minutes
          return newDate.getHours() === existingDate.getHours() &&
                 newDate.getMinutes() === existingDate.getMinutes() &&
                 newDate.getDate() === existingDate.getDate() &&
                 newDate.getMonth() === existingDate.getMonth() &&
                 newDate.getFullYear() === existingDate.getFullYear();
        })
      );

      if (hasOverlappingSlot) {
        // Get creation date
        const createdAt = existingEvent.createdAt instanceof Timestamp
          ? new Date(existingEvent.createdAt.seconds * 1000)
          : new Date(existingEvent.createdAt || 0);

        // Add to duplicates list
        duplicates.push({
          id: doc.id,
          title: existingEvent.title,
          place: existingEvent.place,
          date: existingTimeSlots[0], // Use first time slot for reference
          createdAt: createdAt
        });

        // Add to conflicting events list for review
        reviewReason.conflictingEvents.push({
          title: existingEvent.title,
          place: existingEvent.place,
          time: Timestamp.fromDate(existingTimeSlots[0])
        });

        // If duplicate is older than 1 day, delete it
        if (createdAt < oneDayAgo) {
          console.log(`Deleting old duplicate event: ${doc.id}`, {
            title: existingEvent.title,
            createdAt: createdAt.toISOString(),
            timeSlots: existingTimeSlots.map(d => d.toISOString())
          });
          await deleteDoc(doc.ref);
        }
      }
    }

    // Update review reason
    reviewReason.hasConflict = reviewReason.conflictingEvents.length > 0;

    // Return result with any remaining duplicates
    // (those created within the last day)
    const recentDuplicates = duplicates.filter(d => d.createdAt >= oneDayAgo);
    
    if (duplicates.length > 0) {
      console.log('Duplicate check results:', {
        eventName,
        eventPlace,
        eventDates: eventDates.map(d => d.toISOString()),
        duplicatesFound: duplicates.length,
        recentDuplicates: recentDuplicates.length,
        duplicates: duplicates.map(d => ({
          id: d.id,
          title: d.title,
          place: d.place,
          date: d.date.toISOString(),
          createdAt: d.createdAt.toISOString()
        }))
      });
    }

    return {
      isDuplicate: recentDuplicates.length > 0,
      duplicates: duplicates,
      reviewReason
    };
  } catch (error) {
    console.error('Error checking for duplicates:', error);
    return { 
      isDuplicate: false, 
      duplicates: [],
      reviewReason: {
        missingFields: ['Error occurred during duplicate check'],
        hasConflict: false,
        conflictingEvents: []
      }
    };
  }
};

// Helper function to calculate end time based on duration
const calculateEndTime = (startTime: Timestamp, durationMinutes: number): Timestamp => {
  const endDate = new Date(startTime.seconds * 1000);
  endDate.setMinutes(endDate.getMinutes() + (durationMinutes || 120));
  return Timestamp.fromDate(endDate);
};

const inferEventCategory = (title: string): ClassificationResult => {
  const titleLower = title.toLowerCase();
  
  if (titleLower.includes('concert') || titleLower.includes('music') || titleLower.includes('live')) {
    return {
      category: 'Live Show & Music' as EventCategory,
      type: 'Concerts & Gigs'
    };
  }
  if (titleLower.includes('party') || titleLower.includes('club')) {
    return {
      category: 'Party' as EventCategory,
      type: 'Clubbing'
    };
  }
  if (titleLower.includes('art') || titleLower.includes('exhibition') || titleLower.includes('gallery')) {
    return {
      category: 'Art & Exhibitions' as EventCategory,
      type: 'Exhibitions & Galleries'
    };
  }
  if (titleLower.includes('food') || titleLower.includes('dinner') || titleLower.includes('restaurant')) {
    return {
      category: 'Food & Drink' as EventCategory,
      type: 'Special Nights & Menus'
    };
  }
  if (titleLower.includes('market') || titleLower.includes('fair')) {
    return {
      category: 'Markets & Pop-Ups' as EventCategory,
      type: 'Pop-up Market'
    };
  }
  
  // Default fallback
  return {
    category: 'Social Meetups' as EventCategory,
    type: 'Casual Hangouts'
  };
}; 